/**
 * Access-link secrets are captured before React starts and live only in this
 * module's memory. They must never be copied into router state, storage, or a
 * request URL.
 */
let pendingAccessLinkToken: string | null = null;
let pendingAccessLinkProjectSlug: string | null = null;
let pendingReminderOptOutToken: string | null = null;

export function captureAccessLinkFromLocation(
  location: Pick<Location, 'pathname' | 'search' | 'hash'> = window.location,
  history: Pick<History, 'replaceState' | 'state'> = window.history,
): void {
  const route = location.pathname.replace(/\/+$/, '') || '/';
  const projectJoin = /^\/p\/([a-z0-9]+(?:-[a-z0-9]+)*)\/join$/.exec(route);
  const projectPreferences = /^\/p\/([a-z0-9]+(?:-[a-z0-9]+)*)\/email-preferences$/.exec(route);
  if (!projectJoin && !projectPreferences && !['/join', '/email-preferences'].includes(route)) {
    return;
  }

  // Query-string credentials are never accepted. Remove an accidentally
  // supplied token immediately so it is not retained in browser history.
  const search = new URLSearchParams(location.search);
  const hadQueryToken = search.has('token');
  search.delete('token');
  const safeSearch = search.size > 0 ? `?${search.toString()}` : '';
  if (!location.hash) {
    if (hadQueryToken) history.replaceState(history.state, '', `${location.pathname}${safeSearch}`);
    return;
  }

  const token = new URLSearchParams(location.hash.slice(1)).get('token');
  if (token) {
    if (route === '/join' || projectJoin) {
      pendingAccessLinkToken = token;
      pendingAccessLinkProjectSlug = projectJoin?.[1] ?? null;
    } else {
      pendingReminderOptOutToken = token;
    }
  }

  // Remove the complete fragment, including malformed token values, before
  // React, error reporting, help links, or any other UI code can observe it.
  history.replaceState(history.state, '', `${location.pathname}${safeSearch}`);
}

export function pendingAccessLink(): string | null {
  return pendingAccessLinkToken;
}

export function pendingAccessLinkProject(): string | null {
  return pendingAccessLinkProjectSlug;
}

/** Bind a legacy link to the project returned by the server-side resolver. */
export function bindPendingAccessLinkToProject(projectSlug: string): void {
  pendingAccessLinkProjectSlug = projectSlug;
}

export function clearPendingAccessLink(): void {
  pendingAccessLinkToken = null;
  pendingAccessLinkProjectSlug = null;
}

export function pendingReminderOptOut(): string | null {
  return pendingReminderOptOutToken;
}

export function clearPendingReminderOptOut(): void {
  pendingReminderOptOutToken = null;
}

/** Test-only seam; production code captures from window.location in main.tsx. */
export function setPendingAccessLinkForTest(
  token: string | null,
  projectSlug: string | null = null,
): void {
  pendingAccessLinkToken = token;
  pendingAccessLinkProjectSlug = projectSlug;
}

/** Test-only seam; production code captures from window.location in main.tsx. */
export function setPendingReminderOptOutForTest(token: string | null): void {
  pendingReminderOptOutToken = token;
}
